import type { ReactNode } from "react";

export type Column<T> = {
  header: string;
  /** Cell contents for one row. */
  render: (row: T) => ReactNode;
  /** Extra classes for the cell, e.g. to hide a column on small screens. */
  className?: string;
};

type RecordTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage: string;
};

/**
 * Plain read-only table. Scrolls horizontally on its own so the page body
 * never does.
 */
export function RecordTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage,
}: RecordTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            {columns.map((column) => (
              <th
                key={column.header}
                scope="col"
                className={`px-4 py-3 font-medium text-gray-600 ${column.className ?? ""}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="border-b border-gray-100 last:border-b-0"
            >
              {columns.map((column) => (
                <td
                  key={column.header}
                  className={`px-4 py-3 text-gray-900 ${column.className ?? ""}`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
